// ============================================================
// MAXCONST — useProjects Hook
// Hook chính để fetch + paginate projects. Pages dùng hook này.
// ============================================================

import { useState, useEffect, useCallback } from 'react'
import { fetchProjectsByCategory } from '../api'
import type { ProjectDetail, ProjectCategory } from '../types'

const ITEMS_PER_PAGE = 24

interface UseProjectsOptions {
  category: ProjectCategory
  pages?: number
}

interface UseProjectsReturn {
  // Data
  projects: ProjectDetail[]
  currentProjects: ProjectDetail[]

  // State
  loading: boolean
  error: string | null

  // Pagination
  currentPage: number
  totalPages: number
  totalCount: number
  startIndex: number
  endIndex: number
  setCurrentPage: (page: number) => void
  goToNextPage: () => void
  goToPrevPage: () => void
  getPageNumbers: () => (number | '...')[],
}

// Bộ nhớ đệm toàn cục để lưu danh sách dự án và trang hiện tại của từng category
// Giúp hiển thị ngay lập tức khi quay lại trang trước, tối ưu cuộn trang (Scroll Restoration)
const globalProjectsCache: Record<string, ProjectDetail[]> = {}

const getCachedPage = (category: string): number => {
  try {
    const saved = sessionStorage.getItem(`page_cache_${category}`)
    return saved ? parseInt(saved, 10) : 1
  } catch (e) {
    return 1
  }
}

const setCachedPage = (category: string, page: number) => {
  try {
    sessionStorage.setItem(`page_cache_${category}`, page.toString())
  } catch (e) {
    // ignore
  }
}

export const useProjects = ({
  category,
  pages = 10,
}: UseProjectsOptions): UseProjectsReturn => {
  // Khởi tạo state bằng bộ nhớ đệm (nếu có) để tránh hiện loader làm sập chiều cao trang
  const [projects, setProjects] = useState<ProjectDetail[]>(() => {
    return globalProjectsCache[category] || []
  })
  const [loading, setLoading] = useState(() => {
    return !globalProjectsCache[category] || globalProjectsCache[category].length === 0
  })
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPageState] = useState(() => {
    return getCachedPage(category)
  })

  // Đồng bộ state lập tức khi category thay đổi
  useEffect(() => {
    setProjects(globalProjectsCache[category] || [])
    setLoading(!globalProjectsCache[category] || globalProjectsCache[category].length === 0)
    setCurrentPageState(getCachedPage(category))
    setError(null)
  }, [category])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      // Chỉ hiện loading nếu chưa có sẵn dữ liệu trong bộ nhớ đệm
      if (!globalProjectsCache[category] || globalProjectsCache[category].length === 0) {
        setLoading(true)
      }
      setError(null)

      const result = await fetchProjectsByCategory(category, pages)

      if (cancelled) return

      if (result.success && result.data.length > 0) {
        // Lưu vào cache và hiển thị data thật
        globalProjectsCache[category] = result.data
        setProjects(result.data)
        setLoading(false)
      } else {
        // Nếu API thất bại: kết thúc loading và hiển thị thông báo lỗi
        setError(result.message || 'Không thể tải danh sách dự án')
        setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [category, pages]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tính toán phân trang
  const totalPages = Math.max(1, Math.ceil(projects.length / ITEMS_PER_PAGE))
  const safeCurrentPage = Math.min(currentPage, totalPages)
  const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, projects.length)
  const currentProjects = projects.slice(startIndex, endIndex)

  const setCurrentPage = useCallback((page: number) => {
    const valid = Math.max(1, Math.min(page, totalPages))
    setCurrentPageState(valid)
    setCachedPage(category, valid) // Lưu trang hiện tại vào bộ nhớ đệm
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [category, totalPages])

  const goToNextPage = useCallback(() => {
    setCurrentPage(safeCurrentPage + 1)
  }, [safeCurrentPage, setCurrentPage])

  const goToPrevPage = useCallback(() => {
    setCurrentPage(safeCurrentPage - 1)
  }, [safeCurrentPage, setCurrentPage])

  // Tạo mảng số trang dạng cửa sổ trượt (sliding window) chuyên nghiệp
  const getPageNumbers = useCallback((): (number | '...')[] => {
    const pages: (number | '...')[] = []
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
      return pages
    }

    pages.push(1)

    const leftBound = Math.max(2, safeCurrentPage - 2)
    const rightBound = Math.min(totalPages - 1, safeCurrentPage + 2)

    if (leftBound > 2) {
      pages.push('...')
    }

    let start = leftBound
    let end = rightBound
    
    if (safeCurrentPage <= 4) {
      start = 2
      end = 5
    } else if (safeCurrentPage >= totalPages - 3) {
      start = totalPages - 4
      end = totalPages - 1
    }

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < totalPages) {
        pages.push(i)
      }
    }

    if (end < totalPages - 1) {
      pages.push('...')
    }

    pages.push(totalPages)

    return pages
  }, [safeCurrentPage, totalPages])

  return {
    projects,
    currentProjects,
    loading,
    error,
    currentPage: safeCurrentPage,
    totalPages,
    totalCount: projects.length,
    startIndex,
    endIndex,
    setCurrentPage,
    goToNextPage,
    goToPrevPage,
    getPageNumbers,
  }
}
